import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";
import { CCAvatar } from "@pymblecitizencoders/cc-ui";
import "./Tooltip.css";

interface TooltipProps {
	/** The full text shown on hover. Also the accessible name of the content. */
	label: string;
	/**
	 * Shows a portrait above the label, larger than whatever the caller renders
	 * inline. `src` may be undefined — CCAvatar falls back to initials, which is
	 * the normal case for anyone without a photo on file.
	 */
	avatar?: { src?: string; name: string };
	/**
	 * A wide picture across the top — an event banner rather than a portrait.
	 * Separate from `avatar`, which is a circle sized for a face.
	 */
	image?: { src: string };
	/**
	 * Extra lines under the label: when, where, what it is about.
	 *
	 * A node rather than a string so the caller can mark up its own rows. It is
	 * supplementary by definition, so it is NOT part of the accessible name —
	 * the label alone is.
	 */
	details?: React.ReactNode;
	children: React.ReactNode;
	/** Applied to the trigger, so callers keep their own truncation styles. */
	className?: string;
	/**
	 * Where the box sits horizontally against its trigger.
	 *
	 * `center` (the default) suits a small trigger — an avatar, a chip. `start`
	 * lines the box up with the trigger's left edge, which is what a full-width
	 * table cell wants: centred on the cell, the card lands halfway across the
	 * row, nowhere near the words it describes.
	 */
	align?: "center" | "start";
	/**
	 * The trigger contains its own focusable, already-labelled control — a link
	 * or a button — rather than plain text.
	 *
	 * Turns off both the `aria-hidden` on the wrapper and the visually-hidden
	 * label beside it. Hiding a wrapper that holds a focusable element is an
	 * outright accessibility fault: the control stays in tab order while being
	 * absent from the accessibility tree, so it is reachable and unannounced.
	 * The control names itself in that case, and the tooltip is decoration.
	 */
	interactive?: boolean;
}

/** Gap in px between the trigger and the tooltip. */
const OFFSET = 8;

/**
 * Viewport coordinates, translated into whatever `position: fixed` will
 * actually resolve against.
 *
 * Normally that is the viewport and this is a no-op. But ANY transform on an
 * ancestor makes that ancestor the containing block for fixed descendants, and
 * Ionic's own structure.css sets `body { transform: translateZ(0) }`. The
 * tooltip is portalled onto that body, so `top: 400px` meant 400px from the top
 * of the DOCUMENT, not the window — the box sat one scroll-offset away from the
 * row it belonged to, and the further the page was scrolled the further off it
 * was. Measured drift while scrolling: 8px, 88px, 362px, then off screen.
 *
 * Subtracting the host's own rect converts one into the other, and costs
 * nothing when there is no transform (the branch is skipped).
 */
const toHostSpace = (x: number, y: number): { x: number; y: number } => {
	const host = document.body;
	if (getComputedStyle(host).transform === "none") return { x, y };
	const r = host.getBoundingClientRect();
	return { x: x - r.left, y: y - r.top };
};
/** Below this much room above the trigger, flip underneath instead. */
const FLIP_THRESHOLD = 48;
/** Portrait size in the tooltip. The roll renders 26px inline. */
const AVATAR_SIZE = 72;

/**
 * The tooltip currently on screen, if any — its `hide`.
 *
 * Module scope on purpose: every Tooltip is its own component with its own
 * state, so nothing otherwise stops two being open at once. That is not
 * hypothetical. Scrolling with the pointer held still makes the browser fire
 * `mouseenter` on whichever row has moved under it; that row opens its tooltip
 * while the row you left never receives `mouseleave`, so its card stays on
 * screen pointing at nothing. Opening one closes the other.
 */
let openTooltip: (() => void) | null = null;

/**
 * Dark tooltip matching the Event Manager design.
 *
 * Rendered through a portal on `document.body` and positioned `fixed`, NOT as a
 * `::after` on the trigger. The attendees roll is an `overflow: auto` scroll
 * container, so a pseudo-element tooltip is clipped at its edges — worst
 * exactly on the bottom and right-hand rows, where it is most needed. A
 * portalled fixed element escapes the clip entirely.
 *
 * Accessibility: the visible content is `aria-hidden` and the full `label` is
 * rendered in a visually-hidden span alongside, so a screen reader hears the
 * full name once rather than the truncated text. The trigger is deliberately
 * NOT focusable — making five spans per row tabbable would bury the real
 * controls (checkbox, student link, contact actions) in tab order.
 */
export const Tooltip: React.FC<TooltipProps> = ({
	label,
	avatar,
	image,
	details,
	children,
	className,
	align = "center",
	interactive = false,
}) => {
	const triggerRef = useRef<HTMLSpanElement>(null);
	/** Whether the pointer is on the trigger right now. See `place`. */
	const hovering = useRef(false);
	const tipRef = useRef<HTMLSpanElement>(null);
	const [pos, setPos] = useState<{
		x: number;
		y: number;
		below: boolean;
	} | null>(null);

	/**
	 * Put the box where its trigger currently is.
	 *
	 * Used both on hover and on every scroll frame while open, so it always
	 * measures the trigger afresh rather than trusting where it was.
	 */
	const place = useCallback(() => {
		const el = triggerRef.current;
		if (!el || !label) return;

		/* The frame loop calls this continuously, so it has to know when to stop
		   on its own. Without this, `hide` set the position to null and the very
		   next frame put it straight back: the card would not close on mouse-out
		   at all. */
		if (!hovering.current) {
			setPos(null);
			return;
		}
		const r = el.getBoundingClientRect();
		// Scrolled out of view, or unmounted from under us: nothing to point at.
		if (r.bottom < 0 || r.top > window.innerHeight) {
			setPos(null);
			return;
		}

		/* Room needed above. Once the box has been rendered its real height is
		   known, which beats FLIP_THRESHOLD's guess — a rich card with a banner
		   is nearer 200px than the ~30px that constant assumes. */
		const tipHeight = tipRef.current?.getBoundingClientRect().height ?? 0;
		const needed = (tipHeight || FLIP_THRESHOLD) + OFFSET;
		const below = r.top < needed;
		const next = {
			...toHostSpace(
				align === "start" ? r.left : r.left + r.width / 2,
				below ? r.bottom + OFFSET : r.top - OFFSET,
			),
			below,
		};

		// Only ever one on screen. Cheap to call repeatedly: after the first
		// frame `openTooltip` is already this instance.
		if (openTooltip && openTooltip !== hideRef.current) openTooltip();
		openTooltip = hideRef.current;

		// Skip the render when nothing moved — this runs every scroll frame.
		setPos((prev) =>
			prev &&
			prev.x === next.x &&
			prev.y === next.y &&
			prev.below === next.below
				? prev
				: next,
		);
	}, [label, align]);

	const hide = useCallback(() => {
		if (openTooltip === hideRef.current) openTooltip = null;
		setPos(null);
	}, []);

	/* `place` needs to register this instance's `hide`, and `hide` is defined
	   below it. A ref breaks the cycle without making either depend on the
	   other's identity. */
	const hideRef = useRef<() => void>(() => undefined);
	hideRef.current = hide;

	/* A row can be unmounted while its tooltip is open — a filter changes, a
	   decision moves it to another tab. Leave the registry pointing at a dead
	   instance and the next tooltip's attempt to close it does nothing. */
	useEffect(
		() => () => {
			if (openTooltip === hideRef.current) openTooltip = null;
		},
		[],
	);

	/**
	 * Nudge the box back on screen if centring it on the trigger would run it
	 * past a viewport edge, then offset the arrow by the same amount so it keeps
	 * pointing at the trigger. Done by mutating style rather than via state, so
	 * it costs no extra render.
	 */
	useLayoutEffect(() => {
		const el = tipRef.current;
		if (!el || !pos) return;
		el.style.marginLeft = "";
		el.style.removeProperty("--em-tip-arrow");

		const pad = 8;
		/* `documentElement.clientWidth`, NOT `window.innerWidth`.
		
		   `innerWidth` counts the vertical scrollbar; the page's layout box does
		   not. Clamping to it therefore parked a wide tooltip in the scrollbar's
		   gutter — visibly hard against the edge, and past the right edge of the
		   BODY, which is what a fixed box is measured against here once Ionic's
		   transform on body makes it the containing block. The overhang then
		   showed up as a horizontal scrollbar on the page.
		
		   The two agree on a machine with overlay scrollbars, which is why this
		   survived a measured test: the harness reported the same number for
		   both and the shift looked correct. */
		const limit = document.documentElement.clientWidth;
		const r = el.getBoundingClientRect();
		let shift = 0;
		if (r.left < pad) shift = pad - r.left;
		else if (r.right > limit - pad) shift = limit - pad - r.right;

		if (shift) {
			el.style.marginLeft = `${shift}px`;
			el.style.setProperty("--em-tip-arrow", `${-shift}px`);
		}

		/* Flip underneath if the box would clip the top of the window.
		   FLIP_THRESHOLD alone is a guess at the height, and it stopped being a
		   good one once a portrait could make the box ~130px tall instead of ~30.
		   Measuring the rendered box covers both, and any future content.
		   Runs once: `below` is already true on the second pass. */
		if (!pos.below && r.top < pad) {
			const trigger = triggerRef.current?.getBoundingClientRect();
			if (trigger) {
				setPos({
					x: pos.x,
					y: toHostSpace(0, trigger.bottom + OFFSET).y,
					below: true,
				});
			}
		}
	}, [pos]);

	/**
	 * Keep the box on its trigger, every frame, for as long as it is open.
	 *
	 * A tooltip pinned to viewport coordinates goes stale the moment the page or
	 * a container moves under it. Two earlier attempts were not enough:
	 *
	 * Dismissing on scroll did not work, because after a scroll the browser
	 * fires `mouseenter` on whatever is now under the pointer, so the tooltip
	 * immediately reopened — measured against a page still in motion.
	 *
	 * Repositioning from the scroll event did not work either. An animated
	 * scroll delivers ONE `scroll` event and then keeps moving for many frames,
	 * so the box was placed once, early, and left behind: measured drift of
	 * 8px, then 88px, then 362px, at which point it had left the screen while
	 * its row was still visible.
	 *
	 * A frame loop is immune to how the movement was produced — wheel, keyboard,
	 * momentum, a container scrolling, an animation, a resize, a row changing
	 * height above it. It runs only while a tooltip is on screen, and it is one
	 * `getBoundingClientRect` per frame on a single element.
	 */
	const visible = pos !== null;
	useEffect(() => {
		if (!visible) return;
		let frame = requestAnimationFrame(function tick() {
			place();
			frame = requestAnimationFrame(tick);
		});
		return () => cancelAnimationFrame(frame);
	}, [visible, place]);

	return (
		<>
			<span
				ref={triggerRef}
				className={className}
				onMouseEnter={() => {
					hovering.current = true;
					place();
				}}
				onMouseLeave={() => {
					hovering.current = false;
					hide();
				}}
				aria-hidden={interactive ? undefined : "true"}
			>
				{children}
			</span>
			{!interactive && <span className="em-sr-only">{label}</span>}
			{pos &&
				createPortal(
					<span
						ref={tipRef}
						role="tooltip"
						className={`em-tip${pos.below ? " em-tip--below" : ""}${
							avatar ? " em-tip--portrait" : ""
						}${image || details ? " em-tip--rich" : ""}${
							align === "start" ? " em-tip--start" : ""
						}`}
						style={{ left: pos.x, top: pos.y }}
					>
						{image && (
							/* Decorative, like the portrait: the label underneath is
							   the name of the thing. A banner that 404s hides itself
							   rather than leaving a broken-image glyph in the box —
							   these URLs come from the API and the file may be gone. */
							<img
								className="em-tip__image"
								src={image.src}
								alt=""
								onError={(e) => {
									e.currentTarget.style.display = "none";
								}}
							/>
						)}
						{avatar && (
							/* Decorative: the label right below it is the accessible
							   name, and the sr-only span already carries it. An alt
							   here would have a screen reader say the name twice. */
							<CCAvatar
								src={avatar.src}
								name={avatar.name}
								size={AVATAR_SIZE}
								decorative
								className="em-tip__avatar"
							/>
						)}
						<span className="em-tip__label">{label}</span>
						{details && <span className="em-tip__details">{details}</span>}
					</span>,
					document.body,
				)}
		</>
	);
};
